import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Search, UserCog, History, Phone, LogIn, Check, X, DollarSign, Percent } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export const ManageAdminsPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [deleteAdmin, setDeleteAdmin] = useState(null);
  const [newAdmin, setNewAdmin] = useState({ name: "", email: "", phone: "", password: "" });
  const [showPhoneHistory, setShowPhoneHistory] = useState(null);
  const [phoneHistory, setPhoneHistory] = useState(null);
  const [loggingInAs, setLoggingInAs] = useState(null);
  
  // Commission settings modal
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [selectedAdminForCommission, setSelectedAdminForCommission] = useState(null);
  const [commissionSettings, setCommissionSettings] = useState({
    commission_type: "percentage",
    percentage_rate: 10,
    flat_per_ride: 10
  });
  const [savingCommission, setSavingCommission] = useState(false);

  useEffect(() => { fetchAdmins(); }, []);

  const handleLoginAsAdmin = async (admin) => {
    setLoggingInAs(admin.id);
    try {
      // Get impersonation token from backend
      const res = await axios.post(`${API}/superadmin/impersonate/${admin.id}`);
      
      // Store original super admin session
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
      const currentToken = localStorage.getItem("token");
      localStorage.setItem("original_superadmin_user", JSON.stringify(currentUser));
      localStorage.setItem("original_superadmin_token", currentToken);
      localStorage.setItem("is_impersonating", "true");
      
      // Login as the admin
      login(res.data.token, res.data.user);
      toast.success(`Logged in as ${admin.name}`);
      navigate("/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to login as admin");
    } finally {
      setLoggingInAs(null);
    }
  };

  const handleApprove = async (admin) => {
    try {
      await axios.put(`${API}/superadmin/admins/${admin.id}/approve`);
      toast.success(`${admin.name} has been approved`);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve admin");
    }
  };

  const handleReject = async (admin) => {
    try {
      await axios.put(`${API}/superadmin/admins/${admin.id}/reject`);
      toast.success(`${admin.name} has been rejected`);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject admin");
    }
  };

  const openCommissionModal = (admin) => {
    setSelectedAdminForCommission(admin);
    const existing = admin.superadmin_commission || {};
    setCommissionSettings({
      commission_type: existing.commission_type || "percentage",
      percentage_rate: existing.percentage_rate || 10,
      flat_per_ride: existing.flat_per_ride || 10
    });
    setShowCommissionModal(true);
  };

  const handleSaveCommission = async () => {
    setSavingCommission(true);
    try {
      await axios.put(`${API}/superadmin/admins/${selectedAdminForCommission.id}/commission`, commissionSettings);
      toast.success("Commission settings saved");
      setShowCommissionModal(false);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save commission");
    } finally {
      setSavingCommission(false);
    }
  };

  const getApprovalBadge = (status) => {
    const styles = {
      approved: "bg-green-100 text-green-700",
      pending: "bg-yellow-100 text-yellow-700",
      rejected: "bg-red-100 text-red-700",
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || "bg-gray-100 text-gray-700"}`}>
        {status || "pending"}
      </span>
    );
  };

  const fetchAdmins = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/admins`);
      setAdmins(res.data);
    } catch (err) {
      toast.error("Failed to load admins");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async () => {
    if (!newAdmin.name) {
      toast.error("Please enter admin name");
      return;
    }
    
    // Validate: either phone OR (email + password) is required
    if (!newAdmin.phone && (!newAdmin.email || !newAdmin.password)) {
      toast.error("Please provide either phone number OR email with password");
      return;
    }
    
    try {
      await axios.post(`${API}/superadmin/admins`, { 
        name: newAdmin.name,
        phone: newAdmin.phone || null,
        email: newAdmin.email || null,
        password: newAdmin.password || null,
        role: "admin" 
      });
      toast.success("Admin created successfully");
      setShowAddDialog(false);
      setNewAdmin({ name: "", email: "", phone: "", password: "" });
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create admin");
    }
  };

  const handleToggleStatus = async (admin) => {
    try {
      const newStatus = admin.is_active === false ? true : false;
      await axios.put(`${API}/superadmin/admins/${admin.id}/status?is_active=${newStatus}`);
      toast.success(`Admin ${newStatus ? 'enabled' : 'disabled'}`);
      fetchAdmins();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteAdmin = async () => {
    if (!deleteAdmin) return;
    try {
      await axios.delete(`${API}/superadmin/admins/${deleteAdmin.id}`);
      toast.success("Admin deleted successfully");
      setDeleteAdmin(null);
      fetchAdmins();
    } catch (err) {
      toast.error("Failed to delete admin");
    }
  };

  const fetchPhoneHistory = async (admin) => {
    try {
      const res = await axios.get(`${API}/admin/${admin.id}/phone-history`);
      setPhoneHistory(res.data);
      setShowPhoneHistory(admin);
    } catch (err) {
      toast.error("Failed to fetch phone history");
    }
  };

  const filteredAdmins = admins.filter(admin =>
    admin.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    admin.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    admin.phone?.includes(searchTerm)
  );

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="manage-admins-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Manage Admins</h2>
          <p className="text-muted-foreground">{admins.length} admin accounts</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-admin-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Admin
        </Button>
      </div>

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder="Search admins..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
      </div>

      {/* Mobile Cards View */}
      <div className="md:hidden space-y-3">
        {filteredAdmins.map((admin) => (
          <div key={admin.id} className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <UserCog className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium">{admin.name}</p>
                  <p className="text-xs text-muted-foreground">{admin.email}</p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setDeleteAdmin(admin)} className="text-destructive">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{admin.phone || "No phone"}</span>
              <div className="flex items-center gap-2">
                <Switch checked={admin.is_active !== false} onCheckedChange={() => handleToggleStatus(admin)} />
                <span className={`text-xs ${admin.is_active !== false ? 'text-green-600' : 'text-red-600'}`}>
                  {admin.is_active !== false ? 'Active' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-card rounded-xl border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Approval</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Commission</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAdmins.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No admins found</TableCell></TableRow>
            ) : (
              filteredAdmins.map((admin) => (
                <TableRow key={admin.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                        <UserCog className="w-4 h-4 text-purple-600" />
                      </div>
                      <span className="font-medium">{admin.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>{admin.email}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span>{admin.phone || "-"}</span>
                      {admin.phone_history?.length > 0 && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-6 w-6"
                                onClick={() => fetchPhoneHistory(admin)}
                              >
                                <History className="w-3 h-3 text-muted-foreground" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Phone number changed - Click to view history</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getApprovalBadge(admin.approval_status)}
                      {admin.approval_status === "pending" && (
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="h-6 w-6 text-green-600 hover:text-green-700" onClick={() => handleApprove(admin)}>
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-6 w-6 text-red-600 hover:text-red-700" onClick={() => handleReject(admin)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={admin.is_active !== false} onCheckedChange={() => handleToggleStatus(admin)} />
                      <span className={`text-sm ${admin.is_active !== false ? 'text-green-600' : 'text-red-600'}`}>
                        {admin.is_active !== false ? 'Active' : 'Disabled'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-7 text-xs"
                      onClick={() => openCommissionModal(admin)}
                    >
                      <Percent className="w-3 h-3 mr-1" />
                      {admin.superadmin_commission?.commission_type === "flat_per_ride" 
                        ? `₹${admin.superadmin_commission?.flat_per_ride || 10}/ride` 
                        : `${admin.superadmin_commission?.percentage_rate || 10}%`}
                    </Button>
                  </TableCell>
                  <TableCell>{admin.created_at ? new Date(admin.created_at).toLocaleDateString() : "-"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleLoginAsAdmin(admin)}
                              disabled={loggingInAs === admin.id || admin.approval_status === "pending"}
                              data-testid={`login-as-${admin.id}`}
                            >
                              <LogIn className="w-4 h-4 mr-1" />
                              {loggingInAs === admin.id ? "..." : "Login"}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{admin.approval_status === "pending" ? "Approve admin first" : "Login as this admin"}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteAdmin(admin)} className="text-destructive hover:text-destructive">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add Admin Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => {
        setShowAddDialog(open);
        if (!open) {
          // Reset form when closing
          setNewAdmin({ name: "", email: "", phone: "", password: "" });
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Admin</DialogTitle>
            <DialogDescription>
              Create admin with either phone (OTP login) or email/password login
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input 
                value={newAdmin.name} 
                onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} 
                placeholder="Admin Name" 
              />
            </div>
            
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-2">Login Method (choose one):</p>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Option 1: Phone OTP Login</Label>
                  <Input 
                    value={newAdmin.phone} 
                    onChange={(e) => setNewAdmin({ ...newAdmin, phone: e.target.value, email: "", password: "" })} 
                    placeholder="Enter phone number" 
                  />
                </div>
                
                <div className="text-center text-xs text-muted-foreground">— OR —</div>
                
                <div>
                  <Label className="text-xs text-muted-foreground">Option 2: Email/Password Login</Label>
                  <Input 
                    type="email" 
                    value={newAdmin.email} 
                    onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value, phone: "" })} 
                    placeholder="admin@example.com" 
                    className="mb-2"
                  />
                  <Input 
                    type="password" 
                    value={newAdmin.password} 
                    onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} 
                    placeholder="Password (required for email login)" 
                    disabled={!newAdmin.email}
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddDialog(false);
              setNewAdmin({ name: "", email: "", phone: "", password: "" });
            }}>Cancel</Button>
            <Button onClick={handleCreateAdmin}>Create Admin</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteAdmin} onOpenChange={() => setDeleteAdmin(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Admin</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete <strong>{deleteAdmin?.name}</strong>? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAdmin} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Phone History Dialog */}
      <Dialog open={!!showPhoneHistory} onOpenChange={() => { setShowPhoneHistory(null); setPhoneHistory(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Phone Number History</DialogTitle>
            <DialogDescription>
              Phone number change history for {showPhoneHistory?.name}
            </DialogDescription>
          </DialogHeader>
          {phoneHistory && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-xs text-green-600">Current Phone Number</p>
                <p className="font-mono text-lg font-medium text-green-800">{phoneHistory.current_phone}</p>
              </div>

              {phoneHistory.phone_history?.length > 0 ? (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Previous Numbers</p>
                  <div className="space-y-2">
                    {phoneHistory.phone_history.map((entry, idx) => (
                      <div key={idx} className="p-3 bg-muted rounded-lg text-sm">
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-muted-foreground" />
                          <span className="font-mono">{entry.old_phone}</span>
                          <span className="text-muted-foreground">→</span>
                          <span className="font-mono">{entry.new_phone}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Changed: {new Date(entry.changed_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No phone changes recorded</p>
              )}

              {phoneHistory.last_updated && (
                <p className="text-xs text-muted-foreground text-center">
                  Last updated: {new Date(phoneHistory.last_updated).toLocaleString()}
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => { setShowPhoneHistory(null); setPhoneHistory(null); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Commission Settings Modal */}
      <Dialog open={showCommissionModal} onOpenChange={setShowCommissionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Percent className="w-5 h-5" />
              Commission Settings
            </DialogTitle>
            <DialogDescription>
              Set commission that Super Admin charges to {selectedAdminForCommission?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Commission Type</Label>
              <Select 
                value={commissionSettings.commission_type} 
                onValueChange={(val) => setCommissionSettings({...commissionSettings, commission_type: val})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage (%)</SelectItem>
                  <SelectItem value="flat_per_ride">Flat Per Ride (₹)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {commissionSettings.commission_type === "percentage" ? (
              <div>
                <Label>Percentage Rate (%)</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  value={commissionSettings.percentage_rate}
                  onChange={(e) => setCommissionSettings({...commissionSettings, percentage_rate: parseFloat(e.target.value)})}
                  className="mt-1"
                />
              </div>
            ) : (
              <div>
                <Label>Flat Amount (₹ per ride)</Label>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={commissionSettings.flat_per_ride}
                  onChange={(e) => setCommissionSettings({...commissionSettings, flat_per_ride: parseFloat(e.target.value)})}
                  className="mt-1"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommissionModal(false)}>Cancel</Button>
            <Button onClick={handleSaveCommission} disabled={savingCommission}>
              {savingCommission ? "Saving..." : "Save Commission"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageAdminsPage;
